import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import { DateTime } from "luxon";

// One reading of a class's waitlist block for every surface that shows a Join
// button (day card, week popover, session page), so "Full" turns into "Join
// waitlist" the same way everywhere. The server's `waitlist` block on each class
// payload is the single source: enabled, mode, count/maxSize, the viewer's rank
// and hold.
//
// Two modes, two promises. "race": everyone is told when a seat frees and the
// first to book takes it — so no rank is shown, none is kept. "ordered": the seat
// is offered to the first in line and held for them, so the rank is real and a
// live hold is the headline.
export const waitlistState = (cls) => {
  const wl = cls?.waitlist || {};
  const isFull = cls?.spotsAvailable === 0;
  const { eligible, needsAssessment } = cls?.eligibility || {};
  const listed = !!wl.isListed;
  const ordered = wl.mode === "ordered";
  const offer = wl.offer?.expiresAt ? wl.offer : null;
  return {
    // The waitlist button only ever stands in for a FULL session's Join: once a
    // seat opens, Join comes back (that is the whole point) and a listed viewer
    // gets a small Leave beside it (`spotOpen`).
    show: !!wl.enabled && isFull && (listed || (!cls?.isJoined && eligible !== false && !needsAssessment)),
    spotOpen: !!wl.enabled && listed && !isFull && !cls?.isJoined,
    listed,
    ordered,
    offer,
    position: ordered ? wl.position || null : null,
    count: wl.count || 0,
    maxSize: wl.maxSize || 0,
    listFull: !!wl.isListFull,
    closed: !!wl.closed,
    closeMinutesBefore: wl.closeMinutesBefore || 0
  };
};

// "4:30 PM" today, "Thu 4:30 PM" otherwise — the hold deadline in the viewer's clock.
export const holdUntilLabel = (iso) => {
  const at = DateTime.fromISO(iso);
  if (!at.isValid) return "";
  return at.hasSame(DateTime.now(), "day") ? at.toFormat("h:mm a") : at.toFormat("ccc h:mm a");
};

// The status line under the price: what the viewer is to this session.
export const waitlistCaption = (cls) => {
  const s = waitlistState(cls);
  if (s.spotOpen) {
    if (s.offer) return `Held for you until ${holdUntilLabel(s.offer.expiresAt)}`;
    return s.ordered ? `Spot open · you're #${s.position || "?"}` : "Spot open · first to book gets it";
  }
  if (s.listed) return s.ordered && s.position ? `On waitlist · #${s.position}` : "On waitlist";
  if (s.show && s.count > 0) return `Full · ${s.count} waiting`;
  return null;
};

// Why a disabled waitlist button is disabled — the button alone said nothing.
const closeLabel = (minutes) => {
  if (!minutes) return "at the start";
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes === 60 ? "" : "s"} before the start`;
  return `${minutes} minutes before the start`;
};
export const waitlistBlockedReason = (s) => {
  if (s.listFull) return `The waitlist is full (${s.maxSize} people). Check back — spots free up when someone leaves.`;
  if (s.closed) return `This waitlist closed ${closeLabel(s.closeMinutesBefore)}. Contact the club if you'd still like to join.`;
  return "";
};

// "Leave waitlist" beside Join once a seat has opened — an outlined red button
// placed before Join, sized by the surface (card, popover, class page).
export function LeaveWaitlistLink({ cls, onLeave, stopPropagation = false, fullWidth = false, size = "small", sx = {} }) {
  if (!waitlistState(cls).spotOpen) return null;
  return (
    <Button
      size={size}
      variant="outlined"
      color="error"
      fullWidth={fullWidth}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onLeave?.(cls);
      }}
      sx={{ whiteSpace: "nowrap", ...sx }}
    >
      Leave waitlist
    </Button>
  );
}

// Join / Leave the waitlist. Replaces the Join button when the session is full
// and the service runs a waitlist; a listed viewer sees Leave instead.
export function WaitlistButton({ cls, onJoin, onLeave, size = "small", fullWidth = false, icons = true, sx = {}, stopPropagation = false }) {
  const s = waitlistState(cls);
  if (!s.show) return null;

  const handle = (fn) => (e) => {
    if (stopPropagation) e.stopPropagation();
    fn?.(cls);
  };

  if (s.listed) {
    return (
      <Button size={size} variant="outlined" color="error" fullWidth={fullWidth} onClick={handle(onLeave)} sx={{ whiteSpace: "nowrap", ...sx }}>
        Leave waitlist
      </Button>
    );
  }

  // On a full session this is the page's main action, so it gets Join's weight.
  const blocked = waitlistBlockedReason(s);
  const button = (
    <Button
      size={size}
      variant={blocked ? "outlined" : "contained"}
      color="primary"
      fullWidth={fullWidth}
      disabled={!!blocked}
      startIcon={blocked || !icons ? null : <HourglassEmptyRoundedIcon />}
      onClick={handle(onJoin)}
      sx={{ whiteSpace: "nowrap", ...(!blocked && { color: "common.white" }), ...sx }}
    >
      {s.listFull ? "Waitlist full" : s.closed ? "Waitlist closed" : "Join waitlist"}
    </Button>
  );
  if (!blocked) return button;
  return (
    <Tooltip title={blocked} arrow enterTouchDelay={0}>
      <Box component="span" onClick={(e) => stopPropagation && e.stopPropagation()} sx={{ display: fullWidth ? "block" : "inline-block" }}>
        {button}
      </Box>
    </Tooltip>
  );
}

export default WaitlistButton;
